import { MapTheme } from "@biggeo/bg-server-lib/studio";
import {
    Box,
    Button,
    Divider,
    FlexScrollArea,
    FlexScrollAreaContainer,
    Grid,
    Stack,
    TextField,
    Typography,
    MapDrawerLayout,
} from "@biggeo/bg-ui/lab";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";
import * as turf from "@turf/turf";
import mapboxgl, { GeoJSONSource } from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { mapboxTokenFromEnv } from "../../config/env.config";
import { getMapStyleSprite } from "@biggeo/bg-utils";

mapboxgl.accessToken = mapboxTokenFromEnv();

type GeoFeature = GeoJSON.Feature<GeoJSON.Geometry>;

const MapPlayground: React.FC = () => {
    const mapRef = useRef<mapboxgl.Map | null>(null);
    const drawRef = useRef<MapboxDraw | null>(null);

    const [geojsonInput, setGeojsonInput] = useState<string>("");
    const [intersectionJson, setIntersectionJson] = useState<GeoFeature | null>(
        null
    );
    const [differenceJson, setDifferenceJson] = useState<GeoFeature | null>(
        null
    );
    const [_selectedJson, setSelectedJson] = useState<GeoFeature | null>(null);

    const updateResults = useCallback(() => {
        const draw = drawRef.current;
        const map = mapRef.current;
        if (!draw || !map) return;

        const { features } = draw.getAll();
        const polys = features.filter(
            (f) => f.geometry.type === "Polygon"
        ) as GeoJSON.Feature<
            GeoJSON.Polygon | GeoJSON.MultiPolygon,
            GeoJSON.GeoJsonProperties
        >[];

        setIntersectionJson(null);
        setDifferenceJson(null);
        setSelectedJson(null);

        if (polys.length >= 2) {
            const [p1, p2] = polys;
            const turfCollection = turf.featureCollection([p1, p2]);
            const intersected = turf.intersect(
                turfCollection
            ) as GeoFeature | null;
            const diff = turf.difference(turfCollection) as GeoFeature | null;

            const intersectionSource = map.getSource(
                "intersection"
            ) as GeoJSONSource;
            const differenceSource = map.getSource(
                "difference"
            ) as GeoJSONSource;

            intersectionSource.setData(
                intersected ?? { type: "FeatureCollection", features: [] }
            );
            differenceSource.setData(
                diff ?? { type: "FeatureCollection", features: [] }
            );

            if (intersected) setIntersectionJson(intersected);
            if (diff) setDifferenceJson(diff);
        }
    }, []);

    const loadGeoJson = () => {
        const draw = drawRef.current;
        if (!draw) return;
        try {
            const parsed = JSON.parse(geojsonInput);
            draw.deleteAll();

            if (
                parsed.type === "FeatureCollection" &&
                Array.isArray(parsed.features)
            ) {
                for (const feat of parsed.features) {
                    draw.add(feat);
                }
            } else if (parsed.type === "Feature") {
                draw.add(parsed);
            }

            updateResults();
        } catch (error) {
            console.error("Invalid GeoJSON", error);
        }
    };

    useEffect(() => {
        if (mapRef.current) return;

        const style = MapTheme.light
            ? getMapStyleSprite(MapTheme.light)
            : undefined;
        const map = new mapboxgl.Map({
            container: "map",
            style,
            doubleClickZoom: false,
            minZoom: 1,
            projection: "mercator",
            refreshExpiredTiles: false,
            maxTileCacheSize: 10000000,
            renderWorldCopies: false,
            preserveDrawingBuffer: true,
        });
        mapRef.current = map;

        map.on("load", () => {
            const draw = new MapboxDraw({
                displayControlsDefault: false,
                controls: { polygon: true, trash: true },
            });
            drawRef.current = draw;
            map.addControl(draw);

            map.addSource("intersection", {
                type: "geojson",
                data: { type: "FeatureCollection", features: [] },
            });
            map.addLayer({
                id: "intersection-fill",
                type: "fill",
                source: "intersection",
                paint: { "fill-color": "#ff0000", "fill-opacity": 0.5 },
            });

            map.addSource("difference", {
                type: "geojson",
                data: { type: "FeatureCollection", features: [] },
            });
            map.addLayer({
                id: "difference-fill",
                type: "fill",
                source: "difference",
                paint: { "fill-color": "#0000ff", "fill-opacity": 0.5 },
            });

            map.on("click", (e) => {
                const feature = (e.features?.[0] as GeoFeature) || null;
                setSelectedJson(feature);
            });
        });

        map.on("draw.create", updateResults);
        map.on("draw.update", updateResults);
        map.on("draw.delete", updateResults);

        return () => map.remove();
    }, [updateResults]);

    return (
        <MapDrawerLayout
            leftOpen={true}
            left={
                <FlexScrollAreaContainer>
                    <FlexScrollArea>
                        <Stack gap={2} sx={{ padding: 2, width: "100%" }}>
                            <Grid
                                container
                                alignItems="center"
                                justifyContent="center"
                                spacing={1}
                            >
                                <Grid item xs>
                                    <TextField
                                        placeholder="Paste GeoJson..."
                                        fullWidth
                                        multiple
                                        min={4}
                                        value={geojsonInput}
                                        onChange={(e) => {
                                            if (e)
                                                setGeojsonInput(e.target.value);
                                        }}
                                    />
                                </Grid>
                                <Grid item>
                                    <Button onClick={loadGeoJson}>Load</Button>
                                </Grid>
                            </Grid>

                            <Divider />
                            <Stack>
                                <Typography variant="body2">
                                    Intersection
                                </Typography>
                                <Typography
                                    variant="body3"
                                    sx={{ whiteSpace: "pre-wrap" }}
                                >
                                    {intersectionJson
                                        ? JSON.stringify(
                                              intersectionJson,
                                              null,
                                              2
                                          )
                                        : "None"}
                                </Typography>
                            </Stack>
                            <Divider />
                            <Stack>
                                <Typography variant="body2">
                                    Difference
                                </Typography>
                                <Typography
                                    variant="body3"
                                    sx={{ whiteSpace: "pre-wrap" }}
                                >
                                    {differenceJson
                                        ? JSON.stringify(
                                              differenceJson,
                                              null,
                                              2
                                          )
                                        : "None"}
                                </Typography>
                            </Stack>
                            <Divider />
                            {/* <Stack>
                                <Typography variant="body2">
                                    Selected Polygon
                                </Typography>
                                <Typography
                                    variant="body3"
                                    sx={{ whiteSpace: "pre-wrap" }}
                                >
                                    {selectedJson
                                        ? JSON.stringify(selectedJson, null, 2)
                                        : "Click a shape"}
                                </Typography>
                            </Stack> */}
                        </Stack>
                    </FlexScrollArea>
                </FlexScrollAreaContainer>
            }
        >
            <Box height="100%" width="100%" sx={{ position: "relative" }}>
                <Box
                    id="map"
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        overflow: "hidden",
                    }}
                />
            </Box>
        </MapDrawerLayout>
    );
};

export default MapPlayground;
